import { OverlayContainer } from '@angular/cdk/overlay';
import { Injectable } from '@angular/core';

/**
 * A CDK `OverlayContainer` that follows the app into a native modal `<dialog>`.
 *
 * ## The problem it solves
 *
 * The stock container appends `.cdk-overlay-container` to `document.body`. The
 * content drawer, however, is a native `<dialog>` opened with `showModal()`,
 * which puts the dialog in the browser's **top layer** and makes everything
 * outside it **inert** — so any CDK-overlay panel opened from inside the drawer
 * (every `<ui-dropdown>`: post type, category picker, card type, date mode,
 * poll duration) is painted *underneath* the dialog and cannot receive events.
 * The trigger looks simply dead.
 *
 * ## What it does
 *
 * `getContainerElement()` is called by the CDK each time an overlay is created.
 * This subclass re-parents the (lazily created, cached) container into the
 * currently open modal dialog when there is one, and back to `document.body`
 * when there is not. A child of the dialog participates in its top layer and is
 * not inert, and because neither the dialog nor the container's ancestors
 * establish a containing block (no transform/filter/perspective on `.drawer`),
 * the container's `position: fixed` keeps viewport coordinates — the position
 * strategies keep working unchanged.
 *
 * The move happens at overlay *creation* time, so a panel opened while the
 * drawer is up lands in the dialog, and the next overlay opened after it closes
 * pulls the container back to the body. Two simultaneous overlays across the
 * two hosts would fight over the one container — no partner-area surface does
 * that today (the drawer's dropdown panels are the only overlays inside it).
 *
 * Escape while a panel is open stays sane for free: `<ui-dropdown>`'s handler
 * calls `preventDefault()` on the keydown before closing, which also cancels
 * the dialog's own Escape close-request, so the unsaved-changes guard is not
 * poked by closing a dropdown.
 */
@Injectable()
export class DialogAwareOverlayContainer extends OverlayContainer {
  override getContainerElement(): HTMLElement {
    const container = super.getContainerElement();
    const host = this.openModalDialog() ?? this._document.body;
    if (container.parentElement !== host) {
      host.appendChild(container);
    }
    return container;
  }

  /** The deepest open modal dialog, or `null` when none is up. */
  private openModalDialog(): HTMLElement | null {
    let dialogs: NodeListOf<HTMLDialogElement>;
    try {
      dialogs = this._document.querySelectorAll<HTMLDialogElement>('dialog:modal');
    } catch {
      // jsdom's selector engine does not know `:modal`; an open dialog is the
      // honest approximation there (specs drive at most one).
      dialogs = this._document.querySelectorAll<HTMLDialogElement>('dialog[open]');
    }
    return dialogs.length ? dialogs[dialogs.length - 1] : null;
  }
}
